import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const techs = [
    "React",
    "Next.js",
    "Flutter",
    "FastAPI",
    "Node.js",
    "PostgreSQL",
    "MongoDB",
    "AWS",
    "Azure",
    "Docker",
    "Kubernetes",
    "TypeScript",
];

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative pt-32 pb-24 md:pt-44 md:pb-32 overflow-hidden"
        >
            {/* Calm blurred gradient blobs */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none -z-10"
            >
                <div className="absolute top-20 -left-20 w-[480px] h-[480px] rounded-full bg-blue-300/30 dark:bg-blue-500/20 blur-[120px] animate-blob" />
                <div
                    className="absolute top-40 right-0 w-[420px] h-[420px] rounded-full bg-violet-300/30 dark:bg-violet-500/20 blur-[120px] animate-blob"
                    style={{ animationDelay: "4s" }}
                />
                <div
                    className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-teal-200/30 dark:bg-teal-500/10 blur-[120px] animate-blob"
                    style={{ animationDelay: "8s" }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-secondary/60 text-xs md:text-sm text-muted-foreground"
                >
                    <Sparkles className="h-3.5 w-3.5" />
                    Chennai-based software engineering studio
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="font-display font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tighter mt-6 max-w-5xl"
                >
                    Building modern{" "}
                    <span className="italic font-medium text-foreground">
                        business software
                    </span>{" "}
                    for growing companies.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mt-8 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed"
                >
                    Thooyra crafts scalable ERPs, accounting platforms,
                    cloud-native applications, mobile apps and digital business
                    ecosystems — designed with calm, engineered to last.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link to="/contact">
                        <Button
                            data-testid="hero-cta-consultation"
                            size="lg"
                            className="rounded-full px-7 h-12 text-base"
                        >
                            Book a consultation
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </Link>
                    <Link to="/services">
                        <Button
                            data-testid="hero-cta-services"
                            size="lg"
                            variant="outline"
                            className="rounded-full px-7 h-12 text-base bg-background/40 backdrop-blur"
                        >
                            View services
                        </Button>
                    </Link>
                </motion.div>

                {/* Tech marquee */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="mt-24"
                >
                    <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-6">
                        Trusted technologies we build with
                    </p>
                    <div className="relative overflow-hidden mask-fade">
                        <div className="flex gap-10 animate-marquee w-max">
                            {[...techs, ...techs].map((t, i) => (
                                <div
                                    key={i}
                                    className="text-sm md:text-base font-medium text-muted-foreground/70 whitespace-nowrap"
                                >
                                    {t}
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>

            <style>{`.mask-fade { -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent); mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent); }`}</style>
        </section>
    );
}
